import React, { memo, useRef } from "react";

type ResizableColumnProps = {
  width: number;
  onResize: (width: number) => void;
  children: React.ReactNode;
  className?: string;
  minWidth?: number;
  columnKey?: string;
  onCascadingResize?: (resizingColumn: string, deltaPercentage: number) => void;
  onClick?: () => void;
  allowWrap?: boolean; // when true, allow multiline wrapping instead of ellipsis
  contentPadding?: number; // padding inside the content area (before resize handle)
};

export const ResizableColumn: React.FC<ResizableColumnProps> = memo(({
  width,
  onResize,
  children,
  className,
  minWidth = 8,
  columnKey,
  onCascadingResize,
  onClick,
  allowWrap = false,
  contentPadding = 0,
}) => {
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    startX.current = e.clientX;
    startWidth.current = width;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    const delta = e.clientX - startX.current;
    const deltaPercentage = (delta / window.innerWidth) * 100;

    if (columnKey && onCascadingResize) {
      onCascadingResize(columnKey, deltaPercentage);
    } else {
      const newWidth = Math.max(minWidth, startWidth.current + deltaPercentage);
      onResize(newWidth);
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        width: `${width}%`,
        display: "flex",
        position: "relative",
        overflow: "hidden",
        whiteSpace: allowWrap ? "normal" : "nowrap",
        textOverflow: allowWrap ? "clip" : "ellipsis",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          whiteSpace: allowWrap ? "normal" : "nowrap",
          textOverflow: allowWrap ? "clip" : "ellipsis",
          wordBreak: allowWrap ? "break-word" : undefined,
          paddingRight: contentPadding > 0 ? contentPadding : undefined,
          display: allowWrap ? "flex" : undefined,
          flexDirection: allowWrap ? "column" : undefined,
          justifyContent: allowWrap ? "center" : undefined,
        }}
      >
        {children}
      </div>
      <div
        onMouseDown={handleMouseDown}
        style={{
          width: "2px",
          cursor: "col-resize",
          backgroundColor: "transparent",
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          right: "5px",
          height: "60%",
          zIndex: 10,
          borderRight: "1px solid rgba(102, 102, 102, 0.3)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderRight = "1px solid #666";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderRight =
            "1px solid rgba(102, 102, 102, 0.3)";
        }}
      />
    </div>
  );
});

export default ResizableColumn;

